import { createContext, ReactNode, useEffect, useState, useContext, useReducer } from 'react'
import { type Node as ReactFlowNode, type Edge } from '@xyflow/react'
import { NodeType } from '../types/node-type.type'
import { getAllNodeTypes } from '../services/node-type.service'
import { getAllPlaybooks, getOnePlaybook, updatePlaybook } from '../services/playbook.service'
import { SpexNode, IPlaybook, IAutoLayout, IPlaybookContext } from '../types/playbook.type'
import { IEvent } from '../types/event.type'
import { buildEdge } from '../utils/edgeBuilder'
import { buildNode } from '../utils/nodeBuilder'
import { AuthContext } from './auth.context'
import { GridPaginationModel } from '@mui/x-data-grid'
import { useToast } from './toast.context'

const initialPaginationModel = {
  page: 0,
  pageSize: 20,
}

export const PlaybookContext = createContext<IPlaybookContext>({
  user: '',
  nodeTypes: [],
  reactFlowNodes: [],
  spexNodes: {},
  edges: [], // ReactFlow Edges, not our Edges
  isLoading: true,
  error: '',
  playbook: undefined,
  playbooks: [],
  paginationModel: initialPaginationModel,
  search: '',
  total: 0,
  layoutOptions: {
    algorithm: 'elk',
    direction: 'TB',
  },
  setUser: () => {},
  setEdges: () => {},
  setReactFlowNodes: () => {},
  setSpexNodes: () => {},
  handleRefresh: (playbook: IPlaybook) => {},
  handleUpdate: (playbook: IPlaybook) => {},
  getPlaybook: () => {},
  refreshList: () => {},
  setPaginationModel: () => {},
  setSearch: () => {},
  setLayoutOptions: () => {},
  getNodeTypes: () => {},
  setPlaybooks: () => {},
  setPlaybook: () => {},
  savePlaybook: () => Promise.resolve(),
  runState: {},
  setRunState: () => {},
  appendEvent: () => {},
})

export const PlaybookProvider = ({ children }: { children: ReactNode }) => {
  const [nodeTypes, setNodeTypes] = useState<NodeType[]>([])
  const [reactFlowNodes, setReactFlowNodes] = useState<ReactFlowNode[]>([])
  const [spexNodes, setSpexNodes] = useState<Record<number, SpexNode>>({})
  const [edges, setEdges] = useState<Edge[]>([])
  const [layoutOptions, setLayoutOptions] = useState<IAutoLayout>({
    algorithm: 'elk',
    direction: 'LR',
  })
  const [user, setUser] = useState('')
  const [error, setError] = useState('')
  const [playbooks, setPlaybooks] = useState<IPlaybook[]>([])
  const [playbook, setPlaybook] = useState<IPlaybook | undefined>()
  const [isLoading, setIsLoading] = useState(false)

  const { showToast } = useToast()

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(initialPaginationModel)
  const [search, setSearch] = useState<string>('')
  const [total, setTotal] = useState<number>(0)

  const [refreshListValue, refreshList] = useReducer((x: number) => x + 1, 0)
  const [runState, setRunState] = useState<Record<string, any>>({})

  const authUser = useContext(AuthContext)?.user

  const getPlaybook = async (playbookId: string) => {
    setIsLoading(true)
    const data = await getOnePlaybook(playbookId)

    if (data.success && data.data) {
      setIsLoading(false)

      setLayoutOptions({ ...data.data.auto_layout_options })
      setPlaybook({ ...data.data })
      setRunState(data.data.custom_state)
      handleUpdate(data.data)
    }

    if (!data.success) {
      setError(data.message)
      setIsLoading(false)
    }
  }

  const getNodeTypes = async () => {
    const data = await getAllNodeTypes()

    if (data.success && data.data) {
      setNodeTypes(data.data)
    } else {
      setError(data.message)
    }
  }

  useEffect(() => {
    if (!authUser) {
      return
    }

    ;(async () => {
      setIsLoading(true)
      const data = await getAllPlaybooks(search, paginationModel.page, paginationModel.pageSize)

      if (data.success && data.data) {
        setIsLoading(false)
        setPlaybooks([...data.data])

        if (data.pagination) {
          setTotal(data.pagination.count)
        }
      }

      if (!data.success) {
        setError(data.message)
        setIsLoading(false)
      }
    })()
  }, [authUser, search, paginationModel.page, paginationModel.pageSize, refreshListValue])

  const handleRefresh = (playbook: IPlaybook) => {
    const isLightMode = (window.localStorage.getItem('mode') || 'light') === 'light'

    if (playbook && playbook.nodes.length) {
      const edges: Edge[] = []
      for (const edge of playbook.edges) {
        edges.push(
          buildEdge(edge.id, edge.source, edge.target, edge.data?.label, playbook?.status === 'running', isLightMode),
        )
      }

      const nodes: SpexNode[] = []
      const newSpexNodes: Record<number, SpexNode> = {}

      for (const node of playbook.nodes) {
        nodes.push(
          buildNode(
            `${node.id}`,
            node.position.x,
            node.position.y,
            node.data,
            // TODO: Handle user-defined types.
            // If it's not a type given in nodeTypeComponents,
            // set 'default' here so that ReactFlow can select a component for it.
            // But then we probably need to put the type in `data` as well
            // so we can find the real type.
            node.type,
            node.view,
          ),
        )
        newSpexNodes[parseInt(node.id)] = node
      }

      setReactFlowNodes(() => nodes)
      setSpexNodes(() => newSpexNodes)
      setEdges(() => edges)
    }
  }

  const handleUpdate = (playbook: IPlaybook) => {
    handleRefresh(playbook)
  }

  useEffect(() => {
    if (!authUser) return

    getNodeTypes()
  }, [authUser])

  const savePlaybook = async (newPlaybook: IPlaybook) => {
    const res = await updatePlaybook(newPlaybook)

    if (res.success && res.data) {
      showToast('success', res.message)
      setPlaybook(() => ({ ...newPlaybook }))
      handleUpdate({ ...newPlaybook })
    }
  }

  /*
   * If the playbookId matches the current playbook,
   * append the event to its list of events.
   */
  const appendEvent = async (playbookId: number, ev: IEvent) => {
    setPlaybook((pb) => {
      if (!pb) return pb
      if (pb.id !== playbookId) return pb

      // A lot of work just to append a new event:
      const allEvents = pb.events || {}
      const runEvents = allEvents[ev.run.id] || {
        eventsDate: ev.created_at,
        events: [],
      }
      const oldList = runEvents.events
      return {
        ...pb,
        events: {
          ...allEvents,
          [ev.run.id]: {
            ...runEvents,
            events: [...oldList, ev],
          },
        },
      }
    })
  }

  return (
    <PlaybookContext.Provider
      value={{
        playbook,
        playbooks,
        user,
        error,
        nodeTypes,
        reactFlowNodes,
        spexNodes,
        edges,
        isLoading,
        layoutOptions,
        refreshList,
        paginationModel,
        setPaginationModel,
        search,
        setSearch,
        total,
        setEdges,
        setReactFlowNodes,
        setSpexNodes,
        setUser,
        handleRefresh,
        handleUpdate,
        setLayoutOptions,
        getPlaybook,
        getNodeTypes,
        setPlaybooks,
        setPlaybook,
        savePlaybook,
        runState,
        setRunState,
        appendEvent,
      }}
    >
      {children}
    </PlaybookContext.Provider>
  )
}
